import "./redeem-reward-scan-code.scss";

import { MessageResponseDto } from "@api/api";
import { Constraint } from "@core/model/constraint.enum";
import { EarnPreference } from "@core/model/earn-preference.enum";
import { useUserIdentityContext } from "@core/services/user-identity.context";
import GridItem, { GridItemModel } from "@core/ui/grid-item/grid-item";
import { GetResponseBranchModel } from "@factory/model/query-model-branch";
import { GetResponseCampaignModel } from "@factory/model/query-model-campaign";
import { GetResponseCouponModel } from "@factory/model/query-model-coupon";
import {
  GetResponseRequestModel,
  RequestCreateData,
} from "@factory/model/query-model-request";
import { useQyGetBranch } from "@factory/query/branch.query";
import { useQyGetCampaign } from "@factory/query/campaign.query";
import { useQyGetCoupon } from "@factory/query/coupon.query";
import { viewAsset } from "@factory/query/google-cloud-storage.query";
import { useQyGetRequest } from "@factory/query/request.query";
import { useQyRedeemRewardV2 } from "@factory/query/reward.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { LabelValue } from "@shared/models/label-value.interface";
import Scan from "@shared/ui/scan/scan";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import useScreenSize from "@shared/utility/screen-size";
import { differenceInSeconds } from "date-fns";
import { isNil } from "lodash-es";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { RadioButton } from "primereact/radiobutton";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useSphereRedeemItemCtx } from "../sphere-redeem-item/sphere-redeem-item.context";

export interface RedeemRewardScanCodeProps {}

export function RedeemRewardScanCode() {
  // DECLARATIONS
  const { isMobileMode } = useScreenSize();
  const { selectedRedeemCampaign, setSelectedRedeemCampaign, setCardAlias, couponData, setCouponData } =
    useSphereRedeemItemCtx();
  const { getUserRole } = useUserIdentityContext();
  const { tenant } = getUserRole();
  const { formatToLocale } = useDateTimeContext();
  const navigate = useNavigate();
  const [scan, setScan] = useState(false);
  const [preference, setPreference] = useState(EarnPreference.POINTS);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [redemptionCode, setRedemptionCode] = useState("");
  const { mapRedeemReward } = useFormApiMapper();
  const [selectedCampaignCode, setSelectedCampaignCode] = useState("");
  const [branchOption, seBranchOption] = useState<LabelValue[]>([]);
  const [requestRedeemData, setRequestRedeemData] =
    useState<RequestCreateData | null>(null);
  const [couponId, setCouponId] = useState("")

  // API DEFINITIONS
  const handleGetApiSuccess = async (response: GetResponseCampaignModel) => {
    const _responseData = response.data || [];

    if (_responseData.length > 0) {
      const updatedResponse = await Promise.all(
        _responseData.map(async (element) => {
          const gridItem = {
            name: element.name,
            image: element.image,
            description: element.description,
            points: element.points,
            stamp: element.stamp,
            expirationDate: element.end_date,
            code: element.code,
            constraint: element.constraint,
          } as GridItemModel;

          if (!element.image) {
            return gridItem;
          }

          const elementFile = await viewAsset(element.image);
          const fileBlob = URL.createObjectURL(elementFile);
          gridItem.image = fileBlob;
          return gridItem;
        })
      );
      setSelectedRedeemCampaign(updatedResponse[0]);
    }
  };
  useQyGetCampaign(
    "",
    1,
    0,
    undefined,
    { code: selectedCampaignCode },
    !!selectedCampaignCode,
    handleGetApiSuccess
  );

  const handleGetCouponSuccess = (response: GetResponseCouponModel) => {
    if (response?.data && response?.data.length > 0) {
      const data = response.data[0]
      setCouponData({
        code: data.code || "",
        valueType: data.value_type || "",
        points: data.points || 0,
        stamps: data.stamps || 0
      })
    }
  }
  useQyGetCoupon("", 1, 0, undefined, {code: couponId}, true, handleGetCouponSuccess)

  const handleGetRequestSuccess = (response: GetResponseRequestModel) => {
    if (!response || response.data?.length === 0) {
      return;
    }

    const responseData = response.data?.[0] as unknown as RequestCreateData;
    const expirationDate = formatToLocale(responseData.expiration);
    const now = new Date();
    const diffInSec = differenceInSeconds(expirationDate, now);

    setCardAlias(responseData.card_series);
    if (responseData?.coupon) {
      setCouponId(responseData?.coupon)
    }

    if (diffInSec > 0) {
      setRequestRedeemData(responseData);
      setSelectedCampaignCode(responseData.campaign);
    }
    // eslint-disable-next-line no-console
    console.log({ responseData, expirationDate, now, diffInSec });
  };
  useQyGetRequest(
    "",
    99,
    0,
    undefined,
    {
      code: redemptionCode ?? "",
    },
    !!redemptionCode,
    handleGetRequestSuccess
  );

  const handleRedeemSuccess = (response: MessageResponseDto) => {
    const data = response as any;

    navigate(
      `../redeem-code-success?points=${data.points}&stamp=${data.stamp}&return=campaign`
    );
  };
  const { mutate: redeem } = useQyRedeemRewardV2(handleRedeemSuccess);

  const handleSuccess = (response: GetResponseBranchModel) => {
    const responseData = response?.data || [];
    if (responseData.length === 0) {
      return;
    }

    setSelectedBranch(responseData?.[0].code);
    const _branchOption = LabelValue.MapArray(
      response?.data || [],
      (item) => item.name,
      (item) => item.code
    );
    seBranchOption(_branchOption);
  };
  useQyGetBranch("", 9999, 0, undefined, undefined, true, handleSuccess);

  // LOCAL FUNCTIONS
  const handleRedeem = () => {
    if (!requestRedeemData || !selectedRedeemCampaign) {
      return;
    }

    const value = mapRedeemReward(
      requestRedeemData.code,
      selectedRedeemCampaign.code,
      selectedBranch,
      tenant,
      preference,
      undefined,
      undefined
    );
    redeem(value);
  };
  const handleSuccessScan = (value: string) => {
    setRedemptionCode(value);
  };
  const handleCloseScan = () => {
    setScan(false);
  };
  const handleBack = () => {
    navigate("/business/campaign");
    setSelectedRedeemCampaign(null);
  };
  const accept = () => {
    handleRedeem();
  };
  const confirmRedeem = () => {
    const paymentDescription =
      preference === EarnPreference.POINTS
        ? `${selectedRedeemCampaign?.points} Points`
        : `${selectedRedeemCampaign?.stamp} Stamp`;
    confirmDialog({
      message: (
        <div>
          Are you sure you want to redeem <b>{selectedRedeemCampaign?.name}</b>{" "}
          with <b>{paymentDescription}</b>?
        </div>
      ),
      header: "Confirmation",
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "accept",
      accept,
    });
  };

  // LOCAL RENDERS
  const radioGroup = (
    <section>
      <p>Select Redemption Preference</p>
      <div className="flex flex-wrap gap-3 mb-6">
        {!isNil(selectedRedeemCampaign) &&
        selectedRedeemCampaign?.points > 0 ? (
          <div className="flex align-items-center">
            <RadioButton
              inputId="preference-point"
              name="preference"
              value={EarnPreference.POINTS}
              onChange={(e) => setPreference(e.value)}
              checked={preference === EarnPreference.POINTS}
            />
            <label htmlFor="preference-point" className="ml-2">
              {EarnPreference.POINTS}
            </label>
          </div>
        ) : null}

        {!isNil(selectedRedeemCampaign) && selectedRedeemCampaign?.stamp > 0 ? (
          <div className="flex align-items-center">
            <RadioButton
              inputId="preference-stamp"
              name="preference"
              value={EarnPreference.STAMP}
              onChange={(e) => setPreference(e.value)}
              checked={preference === EarnPreference.STAMP}
            />
            <label htmlFor="preference-stamp" className="ml-2">
              {EarnPreference.STAMP}
            </label>
          </div>
        ) : null}
      </div>
    </section>
  );
  const footerContent = (
    <div>
      <Button
        className="block w-full mt-2"
        label="Redeem"
        onClick={() => confirmRedeem()}
      />
      <Button
        className="block w-full mt-2"
        label="Close"
        severity="secondary"
        outlined
        onClick={() => handleBack()}
      />
    </div>
  );

  return (
    <div id="RedeemRewardScanCode" className="redeem-reward-scan-code">
      <ConfirmDialog />
      <Dialog
        header="Redeem"
        visible={true}
        style={{ width: "50vw" }}
        onHide={() => {
          handleBack();
        }}
        closeOnEscape={true}
        footer={footerContent}
        closable={false}
        maximized={isMobileMode}
      >
        <div className="m-0">
          {scan ? (
            <Scan
              successScan={handleSuccessScan}
              closeScan={handleCloseScan}
              isCloseOnScan={true}
            />
          ) : null}
          <div className="mt-6"></div>
          <div className="mb-6">
            <div className="p-inputgroup flex-1">
              <FloatLabel>
                <InputText
                  id="redemptionCode"
                  value={redemptionCode}
                  onChange={(e) => setRedemptionCode(e.target.value)}
                  className="text-4xl w-full"
                  autoFocus
                  tabIndex={0}
                />
                <label htmlFor="amount">Enter Redemption Code</label>
              </FloatLabel>

              <span
                className="p-inputgroup-addon cursor-pointer px-8 bg-emerald-500 border border-emerald-400"
                onClick={() => setScan(true)}
              >
                <i className="pi pi-camera text-white"></i>
              </span>
            </div>
          </div>
          {selectedRedeemCampaign?.constraint === Constraint.OR
            ? radioGroup
            : null}

          {selectedRedeemCampaign ? (
            <GridItem campaign={selectedRedeemCampaign} couponData={couponData} />
          ) : null}

          <Dropdown
            value={selectedBranch}
            onChange={(e) => setSelectedBranch(e.value)}
            options={branchOption}
            placeholder="Select a branch"
            className="w-full md:w-14rem mt-6 text-4xl"
          />
        </div>
      </Dialog>
    </div>
  );
}

export default RedeemRewardScanCode;
